import { v4 as uuidv4 } from 'uuid';
import { BusinessUnit } from '@/types/common';
import {
  AGE_DAYS_PER_MONTH,
  ConditionField,
  CONDITION_FIELD_LABEL,
  ConditionOperator,
  isMembershipConditionField,
  MEMBERSHIP_CONDITION_FIELDS,
  MembershipConditionField,
  PackageContentType,
  PackageDetail,
  PackageFormValues,
  PackageRuleCriteria,
  PackageStatus,
  PackageVisibility,
  PackageWriteBody,
  RentType,
} from '@/types/package';

/**
 * Defaults for the TRD §4.1 columns the Create Package design does not
 * expose. They are required by POST/PUT, so the form sends the documented
 * defaults until a settings surface collects them (TRD §9).
 */
export const PACKAGE_FIELD_DEFAULTS = {
  minRentalDays: 90,
  freeDayMinTenureDays: 15,
  dpPaymentDeadlineHours: 24,
  // Kept so the form model still mirrors the detail shape, but the write DTOs
  // do not declare it and `whitelist: true` strips it in transit — sending it
  // is a no-op. See PackageDetail.noShowDpForfeit.
  noShowDpForfeit: true,
  minReturnNoticeDays: 7,
  maxPrepayDays: 7,
  stockAlertThreshold: 10,
} as const;

const DEFAULT_BENEFIT = `<ul><li>Dapatkan prioritas orderan (Dash Job)</li><li>Perlindungan kecelakaan (Dash Protection - BPJS BPU)</li><li>Pelatihan &amp; jenjang karir (Dash Level UP)</li><li>Prioritas layanan bengkel Dash resmi</li><li>Akses penuh ke ekosistem Dash Rent Platform</li></ul>`;

const DEFAULT_TERMS = `<ol><li>Minimal masa sewa adalah 3 bulan berkelanjutan</li><li>Bebas berhenti sewa setelah masa minimum terlewati</li><li>Wajib menginfokan pengembalian unit 7 hari sebelum selesai</li></ol>`;

export const INITIAL_PACKAGE_FORM: PackageFormValues = {
  name: '',
  imageURL: '',
  type: '',
  visibility: PackageVisibility.PUBLIC,
  modelID: '',
  dailyPrice: '',
  // No rows: condition is optional, and a package with none accepts every
  // unit of its model. Seeding a blank row made a new package *look*
  // condition-less while the blank-value rule then refused to save it —
  // optional on the hydrate path, mandatory on the create path.
  conditions: [],
  description: '',
  preOrderEnabled: true,
  maxPreorderCount: '0',
  depositEnabled: true,
  deposit: '',
  depositNonRefundable: '',
  downPaymentEnabled: true,
  minDp: '',
  maxInstallments: '',
  freeDaysEnabled: true,
  minRentalDays: String(PACKAGE_FIELD_DEFAULTS.minRentalDays),
  freeDayMinTenureDays: '',
  freeDayDates: [{ key: 'free-day-0', day: '' }],
  benefit: DEFAULT_BENEFIT,
  termsAndConditions: DEFAULT_TERMS,
};

const toInt = (value: string, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

/**
 * How each membership dimension crosses between the form (a row holding
 * strings) and the criteria object (typed lists). Keyed by
 * `MembershipConditionField`, so a new dimension cannot be added to
 * `MEMBERSHIP_CONDITION_FIELDS` without teaching both directions here — the
 * pair has to stay symmetric, and that is easier to keep true side by side
 * than as matching `if` blocks in two functions.
 */
const MEMBERSHIP_DIMENSIONS: Record<
  MembershipConditionField,
  {
    write: (criteria: PackageRuleCriteria, values: string[]) => void;
    read: (criteria: PackageRuleCriteria) => string[];
  }
> = {
  [ConditionField.BUSINESS_UNIT]: {
    write: (criteria, values) => {
      criteria.businessUnits = values as BusinessUnit[];
    },
    read: (criteria) => criteria.businessUnits ?? [],
  },
  [ConditionField.CLIENT]: {
    write: (criteria, values) => {
      const clientIDs = values
        .map((value) => toInt(value))
        .filter((clientID) => clientID > 0);
      if (clientIDs.length) criteria.clientIDs = clientIDs;
    },
    read: (criteria) =>
      (criteria.clientIDs ?? []).map((clientID) => String(clientID)),
  },
};

/**
 * Compiles the form's condition rows into one AND-group of criteria.
 *
 * The backend has no operator interpreter — each continuous dimension is a
 * min/max range (TRD §4.2), so `≥` becomes `min` and `≤` becomes `max`.
 * "Durasi Model" is entered in months and stored as `ageDays`. Business Unit
 * and Client are membership lists instead: no operator, the row's selection
 * travels as-is. Multiple rows on the same dimension and bound: the last one
 * wins, which the cross-row validation in `validatePackageForm` prevents from
 * happening silently.
 */
export const compileCriteria = (
  values: PackageFormValues
): PackageRuleCriteria => {
  const criteria: PackageRuleCriteria = { version: 1 };

  values.conditions.forEach((condition) => {
    if (isMembershipConditionField(condition.field)) {
      // An empty selection constrains nothing — leave the key off entirely
      // rather than sending `[]`, so the saved criteria say "any" the same way
      // an untouched dimension does.
      if (!condition.values.length) return;

      MEMBERSHIP_DIMENSIONS[condition.field].write(criteria, condition.values);
      return;
    }

    if (!condition.value.trim()) return;

    const bound = condition.operator === ConditionOperator.GTE ? 'min' : 'max';

    if (condition.field === ConditionField.ODOMETER) {
      criteria.odometer = {
        ...criteria.odometer,
        [bound]: toInt(condition.value),
      };
      return;
    }

    criteria.ageDays = {
      ...criteria.ageDays,
      [bound]: toInt(condition.value) * AGE_DAYS_PER_MONTH,
    };
  });

  return criteria;
};

/** Turns compiled criteria back into editable rows. */
const decompileCriteria = (criteria?: PackageRuleCriteria) => {
  if (!criteria) return [];

  const rows: PackageFormValues['conditions'] = [];

  const push = (
    field: ConditionField,
    operator: ConditionOperator,
    value: number
  ) => {
    rows.push({
      key: uuidv4(),
      field,
      operator,
      value: String(value),
      values: [],
    });
  };

  /** One row per membership dimension — the whole list lives on that row. */
  const pushMembership = (
    field: MembershipConditionField,
    values: string[]
  ) => {
    rows.push({
      key: uuidv4(),
      field,
      operator: ConditionOperator.LTE,
      value: '',
      values,
    });
  };

  if (criteria.odometer?.min !== undefined) {
    push(ConditionField.ODOMETER, ConditionOperator.GTE, criteria.odometer.min);
  }
  if (criteria.odometer?.max !== undefined) {
    push(ConditionField.ODOMETER, ConditionOperator.LTE, criteria.odometer.max);
  }
  if (criteria.ageDays?.min !== undefined) {
    push(
      ConditionField.DURASI_MODEL,
      ConditionOperator.GTE,
      Math.round(criteria.ageDays.min / AGE_DAYS_PER_MONTH)
    );
  }
  if (criteria.ageDays?.max !== undefined) {
    push(
      ConditionField.DURASI_MODEL,
      ConditionOperator.LTE,
      Math.round(criteria.ageDays.max / AGE_DAYS_PER_MONTH)
    );
  }
  MEMBERSHIP_CONDITION_FIELDS.forEach((field) => {
    const values = MEMBERSHIP_DIMENSIONS[field].read(criteria);
    if (values.length) pushMembership(field, values);
  });

  return rows;
};

/** Builds the POST/PUT body from form state. */
export const toPackageWriteBody = (
  values: PackageFormValues,
  existing?: PackageDetail | null
): PackageWriteBody => {
  const depositEnabled = values.depositEnabled;
  const deposit = depositEnabled ? toInt(values.deposit) : 0;
  const depositNonRefundable = depositEnabled
    ? toInt(values.depositNonRefundable)
    : 0;
  const downPaymentEnabled = depositEnabled && values.downPaymentEnabled;

  const freeDayDates = values.freeDaysEnabled
    ? values.freeDayDates
        .map((entry) => toInt(entry.day, 0))
        .filter((day) => day >= 1 && day <= 31)
    : [];

  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    // '' means "never had one" — send null so the API does not try to relocate
    // an empty string. An unchanged stored URL round-trips untouched.
    imageURL: values.imageURL || null,
    // Status is not a form field: an edit keeps what is stored, and only the
    // status action (packageStatus.tsx) changes it.
    status: existing?.status ?? PackageStatus.ACTIVE,
    modelID: values.modelID,
    contractType: values.type as RentType,
    visibility: values.visibility,
    dailyPrice: toInt(values.dailyPrice),

    deposit,
    depositNonRefundable,
    minDp: downPaymentEnabled ? toInt(values.minDp) : 0,
    maxInstallments: downPaymentEnabled ? toInt(values.maxInstallments) : 0,

    minRentalDays: toInt(
      values.minRentalDays,
      PACKAGE_FIELD_DEFAULTS.minRentalDays
    ),
    freeDayMinTenureDays: values.freeDaysEnabled
      ? toInt(
          values.freeDayMinTenureDays,
          PACKAGE_FIELD_DEFAULTS.freeDayMinTenureDays
        )
      : 0,
    freeDayDates,

    // minBookingLeadDays / maxBookingLeadDays are deliberately NOT sent. The
    // design collects neither, and the API owns their defaults (2 / 14) —
    // omitting them means "default" on create and "keep stored" on edit, so
    // the numbers live in one place instead of drifting per client.
    // Not collected by the design — documented defaults, preserved on edit.
    dpPaymentDeadlineHours:
      existing?.dpPaymentDeadlineHours ??
      PACKAGE_FIELD_DEFAULTS.dpPaymentDeadlineHours,
    noShowDpForfeit:
      existing?.noShowDpForfeit ?? PACKAGE_FIELD_DEFAULTS.noShowDpForfeit,
    minReturnNoticeDays:
      existing?.minReturnNoticeDays ??
      PACKAGE_FIELD_DEFAULTS.minReturnNoticeDays,
    maxPrepayDays:
      existing?.maxPrepayDays ?? PACKAGE_FIELD_DEFAULTS.maxPrepayDays,
    stockAlertThreshold:
      existing?.stockAlertThreshold ??
      PACKAGE_FIELD_DEFAULTS.stockAlertThreshold,
    maxPreorderCount: values.preOrderEnabled
      ? toInt(values.maxPreorderCount)
      : 0,

    rules: [{ criteria: compileCriteria(values) }],
    content: [
      {
        type: PackageContentType.BENEFIT,
        title: 'Benefit',
        body: values.benefit,
      },
      {
        type: PackageContentType.TNC,
        title: 'Syarat & Ketentuan',
        body: values.termsAndConditions,
      },
    ],
  };
};

/** Hydrates form state from a detail response. */
export const fromPackageDetail = (detail: PackageDetail): PackageFormValues => {
  const benefit = detail.content?.find(
    (block) => block.type === PackageContentType.BENEFIT
  );
  const terms = detail.content?.find(
    (block) => block.type === PackageContentType.TNC
  );
  const rows = decompileCriteria(detail.rules?.[0]?.criteria);

  return {
    name: detail.name ?? '',
    imageURL: detail.imageURL ?? '',
    type: detail.contractType,
    // Older packages omit it; the API reads absent as PUBLIC.
    visibility: detail.visibility ?? PackageVisibility.PUBLIC,
    modelID: detail.modelID,
    dailyPrice: String(detail.dailyPrice ?? ''),
    // No fallback row: a package saved with bare `{ version: 1 }` criteria
    // genuinely accepts every unit of the model, and seeding a blank row here
    // would redraw that as an unfinished form. Zero rows renders the explicit
    // "matches every unit" empty state instead.
    conditions: rows,
    description: detail.description ?? '',

    preOrderEnabled: (detail.maxPreorderCount ?? 0) > 0,
    maxPreorderCount: String(detail.maxPreorderCount ?? 0),

    depositEnabled: (detail.deposit ?? 0) > 0,
    deposit: String(detail.deposit ?? ''),
    depositNonRefundable: String(detail.depositNonRefundable ?? ''),

    downPaymentEnabled: (detail.minDp ?? 0) > 0,
    minDp: String(detail.minDp ?? ''),
    maxInstallments: String(detail.maxInstallments ?? ''),

    freeDaysEnabled: (detail.freeDayDates?.length ?? 0) > 0,
    minRentalDays: String(
      detail.minRentalDays ?? PACKAGE_FIELD_DEFAULTS.minRentalDays
    ),
    freeDayMinTenureDays: String(detail.freeDayMinTenureDays ?? ''),
    freeDayDates: (detail.freeDayDates?.length ? detail.freeDayDates : []).map(
      (day) => ({ key: uuidv4(), day: String(day) })
    ),

    benefit: benefit?.body ?? '',
    termsAndConditions: terms?.body ?? '',
  };
};

// Compared through the write body, so a disabled deposit counts as 0 as the
// API reads it.
const HELD_PRICE_FIELDS = [
  { key: 'dailyPrice', label: 'harga sewa' },
  { key: 'deposit', label: 'deposit' },
  { key: 'minDp', label: 'down payment' },
] as const;

export const changedHeldPriceFields = (
  values: PackageFormValues,
  existing: PackageDetail
): string[] => {
  const body = toPackageWriteBody(values, existing);
  return HELD_PRICE_FIELDS.filter(({ key }) => body[key] !== existing[key]).map(
    ({ label }) => label
  );
};

export type PackageFormErrors = Partial<
  Record<keyof PackageFormValues, string>
>;

/**
 * The single error a condition row set can carry, or undefined when it is
 * sound. Split out of `validatePackageForm` so each rule reads as one early
 * return rather than a cascade of flags.
 */
const validateConditions = (values: PackageFormValues): string | undefined => {
  const emptyRow = values.conditions.some((condition) =>
    isMembershipConditionField(condition.field)
      ? !condition.values.length
      : !condition.value.trim()
  );
  if (emptyRow) return 'Setiap condition wajib memiliki nilai';

  // At most one lower and one upper bound per range dimension, lower <= upper.
  // A membership dimension holds its whole list on one row, so its signature
  // ignores the operator — a second row would silently overwrite the first in
  // `compileCriteria`.
  const seen = new Set<string>();
  let duplicateRange = false;
  let duplicateMembership: ConditionField | undefined;
  values.conditions.forEach((condition) => {
    const isMembership = isMembershipConditionField(condition.field);
    const signature = isMembership
      ? condition.field
      : `${condition.field}:${condition.operator}`;
    if (seen.has(signature)) {
      if (isMembership) duplicateMembership = condition.field;
      else duplicateRange = true;
    }
    seen.add(signature);
  });

  if (duplicateMembership) {
    return `${CONDITION_FIELD_LABEL[duplicateMembership]} hanya boleh punya satu baris — pilih beberapa nilai dalam baris yang sama`;
  }
  if (duplicateRange) {
    return 'Satu dimensi hanya boleh punya satu batas bawah dan satu batas atas';
  }

  const criteria = compileCriteria(values);
  const invalidRange = (range?: { min?: number; max?: number }) =>
    range?.min !== undefined &&
    range?.max !== undefined &&
    range.min > range.max;
  if (invalidRange(criteria.odometer) || invalidRange(criteria.ageDays)) {
    return 'Batas bawah tidak boleh melebihi batas atas';
  }

  return undefined;
};

/**
 * Mirrors the 422s the API documents, so the user sees them before a round
 * trip: RTO eligibility is server-side only, but the instalment invariant and
 * the rule-bound conflicts are all checkable here.
 */
export const validatePackageForm = (
  values: PackageFormValues,
  options: { rtoEligible?: boolean } = {}
): PackageFormErrors => {
  const errors: PackageFormErrors = {};

  if (!values.name.trim()) errors.name = 'Package Name wajib diisi';
  if (!values.type) errors.type = 'Variant Type wajib dipilih';
  if (!values.modelID) errors.modelID = 'Model wajib dipilih';
  if (!values.dailyPrice.trim()) {
    errors.dailyPrice = 'Harga sewa per hari wajib diisi';
  }

  // RTO × non-RTO-eligible model is a documented 422.
  if (
    values.type === RentType.RTO &&
    values.modelID &&
    options.rtoEligible === false
  ) {
    errors.type = 'Model ini tidak eligible untuk Rent To Own';
  }

  const conditionError = validateConditions(values);
  if (conditionError) errors.conditions = conditionError;

  if (values.depositEnabled) {
    if (!values.deposit.trim()) {
      errors.deposit = 'Deposit total wajib diisi';
    }
    if (
      values.depositNonRefundable.trim() &&
      toInt(values.depositNonRefundable) > toInt(values.deposit)
    ) {
      errors.depositNonRefundable =
        'Deposit non-refundable tidak boleh melebihi deposit total';
    }
  }

  if (values.depositEnabled && values.downPaymentEnabled) {
    if (values.minDp.trim() && toInt(values.minDp) > toInt(values.deposit)) {
      errors.minDp = 'Down payment tidak boleh melebihi deposit total';
    }
    if (!values.maxInstallments.trim()) {
      errors.maxInstallments = 'Cicilan deposit wajib diisi';
    } else if (
      // TRD C2: the instalments must complete within the contract minimum.
      toInt(values.maxInstallments) > toInt(values.minRentalDays)
    ) {
      errors.maxInstallments =
        'Cicilan deposit harus selesai dalam minimal masa sewa kontrak';
    }
  }

  if (!values.minRentalDays.trim()) {
    errors.minRentalDays = 'Minimal masa sewa kontrak wajib diisi';
  }

  if (values.freeDaysEnabled) {
    const days = values.freeDayDates.filter((entry) => entry.day.trim());
    if (!days.length) {
      errors.freeDayDates = 'Pilih minimal satu tanggal hari gratis';
    } else if (
      days.some((entry) => toInt(entry.day) < 1 || toInt(entry.day) > 31)
    ) {
      errors.freeDayDates = 'Tanggal harus antara 1 dan 31';
    } else if (
      new Set(days.map((entry) => toInt(entry.day))).size !== days.length
    ) {
      errors.freeDayDates = 'Tanggal hari gratis tidak boleh duplikat';
    }
    if (!values.freeDayMinTenureDays.trim()) {
      errors.freeDayMinTenureDays = 'Minimal masa sewa wajib diisi';
    }
  }

  return errors;
};
